import React, { useMemo } from 'react';
import { Text } from '@tamagui/core';
import { XStack, YStack } from '@tamagui/stacks';
import { CustomButton } from '@/components';
import { COLORS } from '@/constants/colors';
import { FONT_SIZE_SECTION, ITEM_GAP } from '../constants';
import GalleryPhoto from './GalleryPhoto';
import LockedPhoto from './LockedPhoto';
import type { UserPhotoView } from '@/api/types';

type GalleryPhotoData = {
  url: string;
  blurUrl: string;
  isLocked: boolean;
};

type GallerySectionProps = {
  photos: UserPhotoView[] | string[];
  onPhotoPress: (index: number) => void;
  onUnlockPress: () => void;
  isDark?: boolean;
};

const GallerySection = React.memo<GallerySectionProps>(
  ({ photos, onPhotoPress, onUnlockPress, isDark }) => {
    const textColor = isDark ? COLORS.WHITE : COLORS.ZINC_900;

    // Normalize photos to array of { url, blurUrl, isLocked }
    const normalizedPhotos: GalleryPhotoData[] = useMemo(() => {
      return photos.map(photo => {
        if (typeof photo === 'string') {
          // Legacy string format - default to unlocked
          return { url: photo, blurUrl: photo, isLocked: false };
        }
        // UserPhotoView object - use blur URL for locked photos
        return {
          url: photo.originalUrl,
          blurUrl:
            photo.largeBlurUrl || photo.mediumBlurUrl || photo.originalUrl,
          isLocked: photo.isLocked,
        };
      });
    }, [photos]);

    // Check if there are any locked photos
    const hasLockedPhotos = useMemo(() => {
      return normalizedPhotos.some(p => p.isLocked);
    }, [normalizedPhotos]);

    return (
      <YStack gap={ITEM_GAP}>
        <Text fontSize={FONT_SIZE_SECTION} fontWeight="700" color={textColor}>
          Galeria Zdjęć
        </Text>
        <XStack flexWrap="wrap" gap={ITEM_GAP}>
          {normalizedPhotos.map((photo, index) => {
            return (
              <YStack key={index} width="48%" flexBasis="48%">
                {!photo.isLocked ? (
                  <GalleryPhoto
                    imageUri={
                      photo.url ||
                      `https://static.photos/people/1200x630/${index + 1}`
                    }
                    onPress={() => onPhotoPress(index)}
                    isDark={isDark}
                  />
                ) : (
                  <LockedPhoto
                    imageUri={
                      photo.blurUrl ||
                      `https://static.photos/people/1200x630/${index + 1}`
                    }
                    onPress={onUnlockPress}
                    isDark={isDark}
                  />
                )}
              </YStack>
            );
          })}
        </XStack>
        {hasLockedPhotos && (
          <YStack alignItems="center" marginTop="$4">
            <CustomButton
              onPress={onUnlockPress}
              borderWidth={2}
              borderColor={COLORS.PRIMARY}
              paddingVertical={10}
              paddingHorizontal={10}
              borderRadius={12}
              color={COLORS.PRIMARY}
              fontWeight="700"
            >
              Odblokuj wszystkie zdjęcia za 50 zetonów
            </CustomButton>
          </YStack>
        )}
      </YStack>
    );
  },
);

GallerySection.displayName = 'GallerySection';

export default GallerySection;
